import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import styles from './NewsDetail.module.css';
import SubPageTop from '../../components/SubPageTop';
import apiService from '../../services/api';

function NewsDetail() {
    const { type, id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // 타입에 따른 제목 설정
    const getTypeTitle = () => {
        switch (type) {
            case 'notice':
                return '공지사항';
            case 'press':
                return '언론보도';
            case 'seminar':
                return '세미나 & 컨퍼런스';
            default:
                return '소식';
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let response;
                
                // 타입에 따라 적절한 API 호출
                if (type === 'notice') {
                    response = await apiService.getNoticeById(id);
                } else if (type === 'press') {
                    response = await apiService.getPressById(id);
                } else if (type === 'seminar') {
                    response = await apiService.getSeminarById(id);
                } else {
                    throw new Error('알 수 없는 타입입니다.');
                }

                // API 응답 데이터를 컴포넌트에서 사용하는 형식으로 변환
                const formattedData = {
                    id: response.id || id,
                    title: response.title || '제목 없음',
                    date: response.date 
                        ? new Date(response.date).toLocaleDateString('ko-KR')
                        : new Date().toLocaleDateString('ko-KR'),
                    img: response.imageUrl || response.image || '/image/20160107000821_0.jpg',
                    content: response.content || response.description || '<p>내용이 없습니다.</p>'
                };

                // 언론보도는 추가 필드 포함
                if (type === 'press') {
                    formattedData.source = response.source || response.publisher || '';
                    const rawLink = response.link || response.originalUrl || response.url || '';
                    // 링크가 있고 http:// 또는 https://로 시작하지 않으면 추가
                    if (rawLink && !rawLink.startsWith('http://') && !rawLink.startsWith('https://')) {
                        formattedData.link = `https://${rawLink}`;
                    } else {
                        formattedData.link = rawLink;
                    }
                }

                setData(formattedData);
            } catch (error) {
                console.error('데이터 가져오기 실패:', error);
                // 에러 발생 시 빈 데이터 설정
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        if (type && id) {
            fetchData();
        }
    }, [type, id]);

    if (loading) {
        return (
            <div className={styles.newsDetailWrap}>
                <SubPageTop title="News" subTitle={getTypeTitle()} backgroundColor="#A9A8E8" />
                <div className={styles.newsDetailContent}>
                    <div className={styles.loading}>데이터를 불러오는 중...</div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={styles.newsDetailWrap}>
                <SubPageTop title="News" subTitle={getTypeTitle()} backgroundColor="#A9A8E8" />
                <div className={styles.newsDetailContent}>
                    <div className={styles.error}>데이터를 찾을 수 없습니다.</div>
                </div>
            </div>
        );
    }

    // 언론보도는 다른 레이아웃 사용
    const isPress = type === 'press';

    return (
        <div className={styles.newsDetailWrap}>
            <SubPageTop title="News" subTitle={getTypeTitle()} backgroundColor="#A9A8E8" />
            <div className={styles.newsDetailContent}>
                {isPress ? (
                    // 언론보도 레이아웃
                    <article className={`${styles.article} ${styles.pressArticle}`}>
                        <header className={styles.pressHeader}>
                            <h1 className={styles.pressTitle}>{data.title}</h1>
                            <div className={styles.pressMeta}>
                                {data.source && (
                                    <span className={styles.source}>출처: {data.source}</span>
                                )}
                                <span className={styles.date}>{data.date}</span>
                            </div>
                        </header>
                        
                        {data.img && (
                            <div className={styles.pressImageWrapper}>
                                <img src={data.img} alt={data.title} className={styles.pressImage} />
                            </div>
                        )}
                        
                        <div 
                            className={styles.pressContent}
                            dangerouslySetInnerHTML={{ __html: data.content }}
                        />
                        
                        {data.link && (
                            <div className={styles.pressLink}>
                                <a href={data.link} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>
                                    원문 보기 : <span>{data.link}</span>
                                </a>
                            </div>
                        )}
                        
                        <div className={styles.actions}>
                            <button 
                                className={styles.backButton}
                                onClick={() => navigate('/news')}
                            >
                                목록으로
                            </button>
                        </div>
                    </article>
                ) : (
                    // 공지사항 & 세미나&컨퍼런스 레이아웃
                    <article className={styles.article}>
                        <header className={styles.header}>
                            <h1 className={styles.title}>{data.title}</h1>
                            <div className={styles.meta}>
                                <span className={styles.date}>{data.date}</span>
                            </div>
                        </header>
                        
                        {data.img && (
                            <div className={styles.imageWrapper}>
                                <img src={data.img} alt={data.title} className={styles.image} />
                            </div>
                        )}
                        
                        <div 
                            className={styles.content}
                            dangerouslySetInnerHTML={{ __html: data.content }}
                        />
                        
                        <div className={styles.actions}>
                            <button 
                                className={styles.backButton}
                                onClick={() => navigate('/news')}
                            >
                                목록으로
                            </button>
                        </div>
                    </article>
                )}
            </div>
        </div>
    );
}

export default NewsDetail;

